// The paper trail, as the app holds it.
//
// `stores/settlement.js` writes a day's settled proposals into `prefs` every
// time it syncs. This is the read side: the refs the screens bind to, and the
// one place that knows which dates "yesterday" and "the last thirty days" are
// — Sydney days, because the pad is printed on Sydney days wherever the phone
// happens to be.
//
// ONE-WAY IMPORT, as everywhere in this folder: this reads settlement's
// output through storage, and nothing reads this. The arithmetic all lives in
// `lib/paper-trail.js`, which is pure and tested; what is left here is the
// reactivity and the clock.

import { computed, ref } from 'vue';
import { prefs } from '../../core/storage.js';
import { demoData, isDemo } from '../../core/demo.js';
import { demoProposalOutcomes } from '../demo-trail.js';
import { localDate } from '../tickets.js';
import { CLASSIC } from '../engine/strategies/classic.js';
import { EVIDENCE_DAYS, compareEvidence, evidenceFor, shiftDate, summariseDay, yesterdayLine } from '../paper-trail.js';

const SYDNEY = 'Australia/Sydney';
const OUTCOMES_KEY = 'proposalOutcomes';
const REJECTED_KEY = 'rejectionOutcomes';
const inBrowser = typeof window !== 'undefined';

/** Every settled day on the device, keyed by 'YYYY-MM-DD'. */
export const outcomes = ref({});

/**
 * And the settled refusal log, in the same shape and read the same way.
 *
 * Its own ref rather than a slice of `outcomes`, for the reason `proposals.js`
 * gives: every consumer of the trail counts a row as something the app said it
 * would bet. Nothing that reads `outcomes` can accidentally read this.
 *
 * /demo has none and prints none. A made-up refusal log would be the app
 * showing a reader an audit of a gate that never ran.
 */
export const rejected = ref({});

/** The day the pad is counted against. A ref so a rollover re-renders. */
export const trailToday = ref(localDate(Date.now(), SYDNEY));

/** Yesterday, on the pad's clock. */
export const trailYesterday = computed(() => shiftDate(trailToday.value, -1));

/**
 * Re-read what settlement last wrote. One localStorage read, so it is cheap
 * enough to run on every 'settled' event rather than trying to work out
 * whether this particular sync touched anything.
 *
 * /demo reads an invented fortnight instead. It has to: a real trail is written
 * one morning at a time by the settle and cannot be back-filled, so without
 * this every pad-record screen on /demo is an empty state. Nothing is written —
 * `syncSettlement` returns before it writes a thing in demo mode — so the real
 * app's own trail is untouched, and `demoData` honours QA's `empty` fixture so
 * the empty state can still be photographed.
 */
export function reloadOutcomes() {
  trailToday.value = localDate(Date.now(), SYDNEY);
  if (isDemo.value) {
    // `demoData` answers the EMPTY ARRAY on QA's `empty` fixture, and the trail
    // is a map of days — so that one answer is read back as the empty map it
    // means rather than handed on as a list nothing here can index.
    const seeded = demoData(demoProposalOutcomes(Date.now())).value;
    outcomes.value = Array.isArray(seeded) ? {} : seeded;
    rejected.value = {};
  } else {
    outcomes.value = prefs[OUTCOMES_KEY] || {};
    rejected.value = prefs[REJECTED_KEY] || {};
  }
  return outcomes.value;
}

/** How many legs the bar refused on a day, settled or not. */
export function refusedOn(date) {
  const list = rejected.value[date];
  return Array.isArray(list) ? list.length : 0;
}

/** Yesterday's pad, counted — or null on a morning after a day with no pad. */
export const yesterdaySummary = computed(() => {
  const day = outcomes.value[trailYesterday.value];
  return Array.isArray(day) && day.length ? summariseDay(day) : null;
});

/** The one sentence that opens History. */
export const yesterdayText = computed(() => yesterdayLine(yesterdaySummary.value));

/** One day's settled proposals, newest first. */
export function proposedOn(date) {
  const list = outcomes.value[date];
  return Array.isArray(list) ? [...list].reverse() : [];
}

/** The last `days` days of settled proposals, flat and newest first. */
export function recentProposed(days = EVIDENCE_DAYS) {
  const out = [];
  for (let i = 0; i < days; i += 1) out.push(...proposedOn(shiftDate(trailToday.value, -i)));
  return out;
}

/**
 * What one strategy has actually done on the pad. A function rather than a
 * map: there are as many strategies as somebody cares to save, and a card
 * asks about exactly one of them.
 *
 * Every strategy but Classic also carries `vsClassic` — its record measured
 * against Classic's over the same window, since Classic is the control group
 * the idea is judged against. Classic's own line has no opponent, so it gets
 * `null` rather than a comparison against itself. The control is computed
 * once per call and reused, not re-walked a second time for the subject.
 */
export function evidence(strategyId, days = EVIDENCE_DAYS) {
  const e = evidenceFor(strategyId, outcomes.value, { days, today: trailToday.value });
  if (strategyId === CLASSIC.id) return { ...e, vsClassic: null };
  const control = evidence(CLASSIC.id, days);
  return { ...e, vsClassic: compareEvidence(e, control) };
}

reloadOutcomes();

if (inBrowser) {
  // Imported here rather than at the top: core/state.js registers window
  // listeners as it loads, so a module-scope import drags a browser into the
  // node test runner. Same reason, same shape as store.js.
  import('../../core/state.js')
    .then(({ bus }) => bus.on('settled', () => reloadOutcomes()))
    .catch(() => {});
}
